/**
 * Normalisation shared by the UI and the import scripts.
 *
 * These rules must not drift between the two. `leadSlug` in particular is the
 * Firestore document id: if the importer and the "New lead" form disagree, the
 * same company ends up as two leads that can never be reconciled.
 *
 * No imports, so `scripts/*.ts` can pull this in directly under tsx.
 */

/** Stable document id derived from the company name. */
export function leadSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/ø/g, 'o')
      .replace(/æ/g, 'ae')
      .replace(/å/g, 'a')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60) || 'lead'
  )
}

/**
 * Free and ISP mail providers. An address at one of these is a personal
 * address, never evidence of a company domain — otherwise the first Gmail
 * contact imported would claim every Gmail signup in the system.
 */
const CONSUMER_MAIL =
  /^(gmail|googlemail|hotmail|outlook|live|msn|yahoo|ymail|icloud|me|mac|aol|protonmail|proton|pm|gmx|web|t-online|freenet|bluewin|sunrise|netplus|orange|wanadoo|free|sfr|laposte|aon|chello|drei|a1|online|start|broadpark|getmail|telia|telenor|altibox|yandex|mail|zoho|fastmail|posteo|tutanota|windowslive|privaterelay|nettet)\./i

/** Hosts that are portals or mail providers rather than a company's own site. */
const NON_COMPANY_HOST = /^(www\.)?(google|facebook|instagram|linkedin|explore-share)\./i

/** Strip scheme, www and path — "https://www.foo.no/bar" → "foo.no". */
export function normaliseWebsite(value: string): string {
  const v = value.trim().toLowerCase()
  if (!v) return ''
  const host = v
    .replace(/^[a-z]+:\/\//, '')
    .replace(/^www\./, '')
    .split(/[/?#]/)[0]
  return host.includes('.') ? host : ''
}

/**
 * Company email domain, or null when the source is a personal address, a
 * portal, or not a domain at all.
 */
export function companyDomain(value: string): string | null {
  const host = value.includes('@')
    ? value.trim().toLowerCase().split('@')[1]
    : normaliseWebsite(value)
  if (!host || !host.includes('.')) return null
  if (CONSUMER_MAIL.test(host) || NON_COMPANY_HOST.test(host)) return null
  return host
}

/**
 * Social handle from either a full profile URL or a bare @handle.
 * "https://www.instagram.com/alpinewelten/" → "alpinewelten".
 */
export function normaliseHandle(value: string): string {
  const v = value.trim()
  if (!v) return ''
  if (/^[a-z]+:\/\//i.test(v) || v.includes('/')) {
    const parts = v
      .replace(/^[a-z]+:\/\//i, '')
      .split(/[?#]/)[0]
      .split('/')
      .filter(Boolean)
    // Drop the host, keep the first path segment.
    return (parts[1] ?? '').replace(/^@/, '')
  }
  return v.replace(/^@/, '')
}

/**
 * Logo URL. The import supplies Google's favicon service as a fallback for
 * companies with no real logo; those are kept — a favicon beats initials — but
 * anything that is not an absolute http(s) URL is dropped rather than rendered
 * as a broken image.
 */
export function normaliseLogo(value: string): string {
  const v = value.trim()
  return /^https?:\/\//i.test(v) ? v : ''
}
